'use client';

import { useState } from 'react';
import Image from "next/image";
import Link from "next/link";
import TestimonialsSlider from "./components/TestimonialsSlider";
import FAQAccordion from "./components/FAQAccordion";
import ContactForm from "./components/ContactForm";
import AutoScrollLogos from "./components/AutoScrollLogos";

export default function Home() {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return (
    <div className="flex flex-col items-start relative w-full min-h-screen">
      {/* Header */}
      <header className="absolute bg-[#f9fafb] border-b border-[#eee] h-[80px] lg:h-[120px] left-0 top-0 w-full z-50">
        <div className="max-w-[1440px] mx-auto h-full flex items-center justify-between px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <Link href="/" className="h-[50px] w-[90px] lg:h-[76px] lg:w-[135px] relative cursor-pointer">
            <Image
              src="/logo.png"
              alt="ADV Online"
              width={135}
              height={76}
              className="object-contain"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-4 lg:gap-[50px]">
            <Link href="#about" className="font-inter font-medium text-[13px] lg:text-[15px] text-black leading-[25px]">
              About Us
            </Link>
            <Link href="#services" className="font-inter font-medium text-[13px] lg:text-[15px] text-black leading-[25px]">
              Our Services
            </Link>
            <Link href="#faq" className="font-inter font-medium text-[13px] lg:text-[15px] text-black leading-[25px]">
              FAQ
            </Link>
          </nav>
          <div className="flex items-center gap-4">
            <a 
              href="#contact" 
              className="hidden md:inline-block bg-[#8436ff] px-4 py-2 lg:px-[35px] lg:py-[15px] rounded-[50px] font-inter font-bold text-[14px] lg:text-[16px] text-white leading-[25px] hover:opacity-90 transition-opacity"
            >
              Contact Us
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-8 h-8 flex flex-col justify-center gap-1.5 cursor-pointer"
              aria-label="Toggle menu"
            >
              <span className={`block h-0.5 w-full bg-black transition-all ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block h-0.5 w-full bg-black transition-all ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block h-0.5 w-full bg-black transition-all ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#f9fafb] border-t border-[#eee] absolute top-full left-0 right-0">
            <nav className="flex flex-col px-5 py-4 gap-4">
              <Link href="#about" className="font-inter font-medium text-[15px] text-black leading-[25px]" onClick={() => setIsMobileMenuOpen(false)}>
                About Us
              </Link>
              <Link href="#services" className="font-inter font-medium text-[15px] text-black leading-[25px]" onClick={() => setIsMobileMenuOpen(false)}>
                Our Services
              </Link>
              <Link href="#faq" className="font-inter font-medium text-[15px] text-black leading-[25px]" onClick={() => setIsMobileMenuOpen(false)}>
                FAQ
              </Link>
              <a 
                href="#contact" 
                className="bg-[#8436ff] px-4 py-3 rounded-[50px] font-inter font-bold text-[16px] text-white leading-[25px] text-center hover:opacity-90 transition-opacity"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="bg-[#f9fafb] min-h-[600px] lg:h-[980px] w-full pt-20 lg:pt-[120px] pb-8 lg:pb-0">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32 flex flex-col lg:flex-row items-center justify-between gap-5 sm:gap-8 lg:gap-0 h-full">
          <div className="flex flex-col gap-5 sm:gap-6 lg:gap-[30px] w-full lg:w-[514px] order-2 lg:order-1">
            <div className="flex flex-col gap-3 sm:gap-4 lg:gap-[15px]">
              <h1 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[44px] leading-[1.15] sm:leading-[1.4] text-[#111827] text-center lg:text-left">
                Drive Leads and Sales: Premium, Full Service Google Ad Agency
              </h1>
              <p className="font-manrope font-normal text-base sm:text-lg lg:text-[18px] leading-normal text-[#6b7280] text-center lg:text-left">
                ADV Online provides bespoke Google Ads services guaranteed to bring you qualified leads for a lower CPA. Watch your revenue soar!
              </p>
            </div>
            <a 
              href="#contact" 
              className="bg-[#8436ff] px-[35px] py-[15px] rounded-[50px] font-inter font-bold text-[16px] text-white leading-[25px] w-full lg:w-fit mx-auto lg:mx-0 text-center inline-block hover:opacity-90 transition-opacity"
            >
              Schedule A Call
            </a>
          </div>
          <div className="h-[300px] sm:h-[400px] md:h-[500px] lg:h-[793px] w-full max-w-full lg:w-[817px] relative order-1 lg:order-2">
            <Image
              src="/main.png"
              alt="Google Ads Dashboard"
              width={817}
              height={793}
              className="object-contain w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="bg-white min-h-[650px] w-full py-8 sm:py-12 lg:py-[60px]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] text-center mb-8 sm:mb-12 lg:mb-[60px]">
            We are trusted worldwide by over 1000 companies
          </h2>
          
          {/* Company Logos */}
          <AutoScrollLogos />

          {/* Stats Cards */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-6 lg:gap-[50px]">
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-10 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px] w-full max-w-[320px] sm:w-[280px] lg:w-[320px] text-center">
              <div className="font-manrope font-extrabold text-[28px] sm:text-4xl lg:text-[40px] leading-[1.4] text-[#8436ff] mb-4 lg:mb-[15px]">
                $250k
              </div>
              <div className="font-manrope font-normal text-base sm:text-lg lg:text-[18px] text-[#6b7280]">
                average Google Ads budget
              </div>
            </div>
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-10 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px] w-full max-w-[320px] sm:w-[280px] lg:w-[320px] text-center">
              <div className="font-manrope font-extrabold text-[28px] sm:text-4xl lg:text-[40px] leading-[1.4] text-[#8436ff] mb-4 lg:mb-[15px]">
                10+
              </div>
              <div className="font-manrope font-normal text-base sm:text-lg lg:text-[18px] text-[#6b7280]">
                years of PPC experience
              </div>
            </div>
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-10 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px] w-full max-w-[320px] sm:w-[280px] lg:w-[320px] text-center">
              <div className="font-manrope font-extrabold text-[28px] sm:text-4xl lg:text-[40px] leading-[1.4] text-[#8436ff] mb-4 lg:mb-[15px]">
                1000+
              </div>
              <div className="font-manrope font-normal text-base sm:text-lg lg:text-[18px] text-[#6b7280]">
                successful ad launches
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Ads Drive Growth Section */}
      <section id="services" className="bg-[#f9fafb] w-full py-8 sm:py-12 md:py-16 lg:py-[128px]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] text-center mb-8 sm:mb-12 lg:mb-[88px]">
            Google Ads Drive Growth
          </h2>
          
          <div className="flex flex-col gap-6 sm:gap-8 lg:gap-[50px]">
            {/* First Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-[50px]">
              <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-10 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px] min-h-[200px] lg:h-[320px] flex flex-col gap-4 sm:gap-6 lg:gap-[30px]">
                <div className="flex gap-4 sm:gap-4 lg:gap-[15px] items-end">
                  <div className="bg-[#8436ff] rounded-[6px] w-[50px] h-[50px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center shrink-0">
                    <Image src="/icons/icon_1.svg" alt="Globe" width={40} height={40} className="w-5 h-5 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                  </div>
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-black leading-normal">
                    Worldwide audience
                  </h3>
                </div>
                <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                  Over 90% of users of the internet are reachable with targeted ads that utilize Google Display Network
                </p>
              </div>
              
              <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px] min-h-[200px] lg:h-[320px] flex flex-col gap-4 sm:gap-6 lg:gap-[30px]">
                <div className="flex gap-3 sm:gap-4 lg:gap-[15px] items-end">
                  <div className="bg-[#8436ff] rounded-[6px] w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center shrink-0">
                    <Image src="/icons/icon_2.svg" alt="Gear" width={40} height={40} className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                  </div>
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-black leading-normal">
                    Granular<br />targeting
                  </h3>
                </div>
                <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                  You can reach your best potential customers by using detailed demographic targeting, including gender, age, location, recent purchases, interests and more
                </p>
              </div>
              
              <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px] min-h-[200px] lg:h-[320px] flex flex-col gap-4 sm:gap-6 lg:gap-[30px]">
                <div className="flex gap-3 sm:gap-4 lg:gap-[15px] items-end">
                  <div className="bg-[#8436ff] rounded-[6px] w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center shrink-0">
                    <Image src="/icons/icon_3.svg" alt="Shield" width={40} height={40} className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                  </div>
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-black leading-normal">
                    You pay only based on clicks
                  </h3>
                </div>
                <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                  No Guessing, no charges based upon empty views. Pay per click ensures spend efficiency.
                </p>
              </div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-[50px]">
              <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px] min-h-[200px] lg:h-[320px] flex flex-col gap-4 sm:gap-6 lg:gap-[30px]">
                <div className="flex gap-3 sm:gap-4 lg:gap-[15px] items-end">
                  <div className="bg-[#8436ff] rounded-[6px] w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center shrink-0">
                    <Image src="/icons/icon_4.svg" alt="Chart" width={40} height={40} className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                  </div>
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-black leading-normal">
                    Scale campaigns<br />with business
                  </h3>
                </div>
                <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                  Launch campaigns with small budgets that fit your needs. As you optimize conversions and grow your business, you can increase your budget to match.
                </p>
              </div>
              
              <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px] min-h-[200px] lg:h-[320px] flex flex-col gap-4 sm:gap-6 lg:gap-[30px]">
                <div className="flex gap-3 sm:gap-4 lg:gap-[15px] items-end">
                  <div className="bg-[#8436ff] rounded-[6px] w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center shrink-0">
                    <Image src="/icons/icon_5.svg" alt="Link" width={40} height={40} className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                  </div>
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-black leading-normal">
                    End-to-End<br />funnels
                  </h3>
                </div>
                <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                  We can create a bespoke sales funnel in Google Ads that engages and tracks your customer on their entire buying journey, from initial awareness to sale.
                </p>
              </div>
              
              <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px] min-h-[200px] lg:h-[320px] flex flex-col gap-4 sm:gap-6 lg:gap-[30px]">
                <div className="flex gap-3 sm:gap-4 lg:gap-[15px] items-end">
                  <div className="bg-[#8436ff] rounded-[6px] w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center shrink-0">
                    <Image src="/icons/icon_6.svg" alt="User" width={40} height={40} className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
                  </div>
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-black leading-normal">
                    Maintain relationships
                  </h3>
                </div>
                <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                  If someone demonstrates an interest - such as with ad clicks or add-to-cart - we can build a retargeting campaign to maintain touch-points and encourage closing sales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="bg-[#f9fafb] w-full py-8 sm:py-12 lg:py-[60px]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <div className="text-center mb-8 sm:mb-12 lg:mb-[60px]">
            <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] mb-4 sm:mb-5 lg:mb-[20px]">
              ADV Online Is Your One-Stop<br className="hidden sm:block" />Google Ads Agency
            </h2>
            <p className="font-manrope font-normal text-base sm:text-lg lg:text-[18px] text-[#6b7280]">
              Our focus isn&apos;t just ads but on achieving your business&apos; sales goals to maximize your success.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-[50px]">
            {/* Row 1 */}
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-10 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px] flex flex-col gap-4 sm:gap-4 lg:gap-[15px]">
              <div className="bg-[#8436ff] rounded-[6px] w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] lg:w-[80px] lg:h-[80px] flex items-center justify-center">
                <Image src="/icons/icon_7.svg" alt="Sliders" width={40} height={40} className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10" />
              </div>
              <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-black leading-normal">
                Precision-tuned performance
              </h3>
              <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                We constantly adjust bids, audiences, and ads—fine-tuning every lever to improve results and lower costs over time.
              </p>
            </div>
            
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-[40px] py-[30px] flex flex-col gap-[15px]">
              <div className="bg-[#8436ff] rounded-[6px] w-[80px] h-[80px] flex items-center justify-center">
                <Image src="/icons/icon_8.svg" alt="Magnifying Glass" width={40} height={40} />
              </div>
              <h3 className="font-manrope font-medium text-[24px] text-black leading-normal">
                Reach the right users
              </h3>
              <p className="font-manrope font-normal text-[15px] text-[#6b7280] leading-normal">
                We use advanced targeting to ensure your ads are shown to the people most likely to become your customers, maximizing relevance and ROI.
              </p>
            </div>

            {/* Row 2 */}
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-[40px] py-[30px] flex flex-col gap-[15px]">
              <div className="bg-[#8436ff] rounded-[6px] w-[80px] h-[80px] flex items-center justify-center">
                <Image src="/icons/icon_9.svg" alt="Wrench" width={40} height={40} />
              </div>
              <h3 className="font-manrope font-medium text-[24px] text-black leading-normal">
                Ongoing optimization
              </h3>
              <p className="font-manrope font-normal text-[15px] text-[#6b7280] leading-normal">
                We relentlessly refine your campaigns to lower costs and boost performance over time.
              </p>
            </div>
            
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-[40px] py-[30px] flex flex-col gap-[15px]">
              <div className="bg-[#8436ff] rounded-[6px] w-[80px] h-[80px] flex items-center justify-center">
                <Image src="/icons/icon_10.svg" alt="Bank" width={40} height={40} />
              </div>
              <h3 className="font-manrope font-medium text-[24px] text-black leading-normal">
                Sales-focused approach
              </h3>
              <p className="font-manrope font-normal text-[15px] text-[#6b7280] leading-normal">
                We optimize for what truly matters: qualified leads, sales, and measurable ROI—not just clicks.
              </p>
            </div>

            {/* Row 3 */}
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-[40px] py-[30px] flex flex-col gap-[15px]">
              <div className="bg-[#8436ff] rounded-[6px] w-[80px] h-[80px] flex items-center justify-center">
                <Image src="/icons/icon_11.svg" alt="User" width={40} height={40} />
              </div>
              <h3 className="font-manrope font-medium text-[24px] text-black leading-normal">
                Tailored campaign strategy
              </h3>
              <p className="font-manrope font-normal text-[15px] text-[#6b7280] leading-normal">
                We build customized Google Ads campaigns focused on your specific goals, target audience, and budget.
              </p>
            </div>
            
            <div className="bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-[40px] py-[30px] flex flex-col gap-[15px]">
              <div className="bg-[#8436ff] rounded-[6px] w-[80px] h-[80px] flex items-center justify-center">
                <Image src="/icons/icon_12.svg" alt="Tree" width={40} height={40} />
              </div>
              <h3 className="font-manrope font-medium text-[24px] text-black leading-normal">
                Clear performance insights
              </h3>
              <p className="font-manrope font-normal text-[15px] text-[#6b7280] leading-normal">
                You&apos;ll receive transparent, easy-to-understand reports and dashboards that show your campaign&apos;s performance and the progress toward your goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Services Timeline Section */}
      <section className="bg-[#f9fafb] w-full py-8 sm:py-12 md:py-16 lg:py-[128px] relative">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] text-center mb-8 sm:mb-12 lg:mb-[92px]">
            Our Services
          </h2>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="hidden lg:block absolute left-1/2 top-[100px] bottom-[70px] w-[6px] bg-[#8436ff] -translate-x-1/2" />

            {/* Timeline Items */}
            <div className="space-y-8 sm:space-y-12 lg:space-y-[100px]">
              {/* Item 1 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:ml-auto lg:mr-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    In-Depth Market Research
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    To develop the most effective strategy for you to maximize market impact, we analyze your market niche, including your competitors. Based on this deep research, we identify market opportunities for your brand.
                  </p>
                </div>
              </div>

              {/* Item 2 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:mr-auto lg:ml-[61px] w-full lg:w-[400px] bg-white border-2 border-[#8436ff] rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    Target Strategies for Success
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    To reach the most purchase-ready audience within your budget and based upon your goals, we build a customized ads strategy. We details the hows, wheres, and whens of a winning media plan.
                  </p>
                </div>
              </div>

              {/* Item 3 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:ml-auto lg:mr-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    Step-by-Step Plan
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    Our strategy is constructed so that you can know where you are on your marketing journey and what is coming next.
                  </p>
                </div>
              </div>

              {/* Item 4 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:mr-auto lg:ml-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-[#111827] mb-3 sm:mb-4 lg:mb-[15px]">
                    Detailed Analytics Dashboad
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    In order to maximize effectiveness you need data designed to measure your campaign KPIs. We build your dashboard based upon your needs, to ensure you can see and measure conversions, clicks, and user interactions in real time.
                  </p>
                </div>
              </div>

              {/* Item 5 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:ml-auto lg:mr-[61px] w-full lg:w-[400px] bg-white border-2 border-[#8436ff] rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-[#111827] mb-3 sm:mb-4 lg:mb-[15px]">
                    Deep Dive Research On Keywords
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    We find the highest performing keywords, guaranteeing search terms, ads, and landing pages work together for maximum impact.
                  </p>
                </div>
              </div>

              {/* Item 6 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:ml-auto lg:mr-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    Ad Creative Development
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    Our highly trained and experienced team are experts in creating high converting design creatives and ad copy. We aim for high CTR at low CPC by motivating your audience.
                  </p>
                </div>
              </div>

              {/* Item 7 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:ml-auto lg:mr-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    Highly Effective Launches
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    A great campaign starts with a great launch. We ensure that from the start the right audience is targeted at the right bids. The goal is always to ensure all elements are in alignment with each other and with the campaign&apos;s objective.
                  </p>
                </div>
              </div>

              {/* Item 8 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:mr-auto lg:ml-[61px] w-full lg:w-[400px] bg-white border-2 border-[#8436ff] rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    Results-Based Accountability
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    We guarantee that you will be constantly updated with KPIs that align with your business and its goals. That includes lead volume, CPA, ROAS, and conversion rates.
                  </p>
                </div>
              </div>

              {/* Item 9 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:mr-auto lg:ml-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-6 sm:px-8 lg:px-[40px] py-6 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-lg sm:text-xl lg:text-[24px] text-[#111827] mb-3 sm:mb-4 lg:mb-[15px]">
                    Web Page Analysis
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    To optimize your landing page for increased conversion rates, and a lower CPA, we begin with a detailed audit of your landing page.
                  </p>
                </div>
              </div>

              {/* Item 10 */}
              <div className="relative flex flex-col lg:flex-row items-center lg:items-center">
                <div className="lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-[50px] h-[50px] sm:w-[60px] sm:h-[60px] lg:w-[68px] lg:h-[68px] bg-[#8436ff] rounded-full border-4 lg:border-8 border-white shadow-[0px_24px_32px_0px_rgba(133,104,212,0.14),0px_16px_24px_0px_rgba(133,104,212,0.1),0px_2px_6px_0px_rgba(133,104,212,0.08),0px_0px_1px_0px_rgba(0,0,0,0.04)] mb-4 lg:mb-0" />
                <div className="lg:ml-auto lg:mr-[61px] w-full lg:w-[400px] bg-white rounded-[12px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-5 sm:px-8 lg:px-[40px] py-8 sm:py-8 lg:py-[30px]">
                  <h3 className="font-manrope font-medium text-[18px] sm:text-xl lg:text-[24px] text-[#111827] mb-4 sm:mb-4 lg:mb-[15px]">
                    Non-Stop Optimizing
                  </h3>
                  <p className="font-manrope font-normal text-sm sm:text-base lg:text-[15px] text-[#6b7280] leading-normal">
                    Campaign maximization is a non-stop process based upon market feedback. We monitor, test, and refine to reduce costs and elevate your ROAS with improved performance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-[#f9fafb] w-full py-8 sm:py-12 md:py-16 lg:py-[128px]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] text-center mb-8 sm:mb-12 lg:mb-[88px]">
            Testimonials
          </h2>

          <TestimonialsSlider />
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-[#f9fafb] w-full py-8 sm:py-12 md:py-16 lg:py-[128px]">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32">
          <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] text-center mb-8 sm:mb-12 lg:mb-[60px]">
            FAQ
          </h2>

          <div className="flex flex-col gap-6 sm:gap-8 lg:gap-[30px] max-w-[1044px] mx-auto">
            <FAQAccordion />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className={`bg-white w-full relative overflow-hidden ${isFormSubmitted ? 'py-32 sm:py-48 lg:py-[600px]' : 'py-16 sm:py-24 md:py-32 lg:py-[300px]'}`}>
        {/* Background ellipses effect from Figma */}
        <div 
          className="absolute flex items-center justify-center right-[-350px] top-[-400px] pointer-events-none"
          style={{
            transform: 'rotate(206.329deg) scaleY(-1)',
            width: '2000px',
            height: '2000px',
            overflow: 'visible',
          }}
        >
          <div className="relative" style={{ width: '1223px', height: '1321px', margin: '-15.14% -16.35%' }}>
            <svg 
              width="1230" 
              height="900" 
              viewBox="0 0 1230 900" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="absolute inset-0"
              style={{ width: '100%', height: '100%', overflow: 'visible' }}
            >
              <g opacity="0.5">
                <g filter="url(#filter0_f_3775_1014)">
                  <circle cx="580.5" cy="580.5" r="580.5" transform="matrix(-0.896259 -0.443531 -0.443531 0.896259 1656.93 -239.981)" fill="url(#paint0_radial_3775_1014)"/>
                </g>
                <g filter="url(#filter1_f_3775_1014)">
                  <circle cx="580.5" cy="580.5" r="580.5" transform="matrix(-0.896259 -0.443531 -0.443531 0.896259 1611.06 -84.1611)" fill="url(#paint1_radial_3775_1014)"/>
                </g>
                <g filter="url(#filter2_f_3775_1014)">
                  <circle cx="491.5" cy="491.5" r="491.5" transform="matrix(-0.896259 -0.443531 -0.443531 0.896259 1475.89 -110.885)" fill="#0099FA"/>
                </g>
                <g filter="url(#filter3_f_3775_1014)">
                  <ellipse cx="573" cy="571.5" rx="573" ry="571.5" transform="matrix(-0.896259 -0.443531 -0.443531 0.896259 1602.37 -240.204)" fill="white"/>
                </g>
              </g>
              <defs>
                <filter id="filter0_f_3775_1014" x="98.5566" y="-757.802" width="1561.26" height="1561.26" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="100" result="effect1_foregroundBlur_3775_1014"/>
                </filter>
                <filter id="filter1_f_3775_1014" x="52.6875" y="-601.981" width="1561.26" height="1561.26" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="100" result="effect1_foregroundBlur_3775_1014"/>
                </filter>
                <filter id="filter2_f_3775_1014" x="125.779" y="-579.978" width="1383.22" height="1383.22" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="100" result="effect1_foregroundBlur_3775_1014"/>
                </filter>
                <filter id="filter3_f_3775_1014" x="62.5049" y="-754.057" width="1545.67" height="1543.84" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feGaussianBlur stdDeviation="100" result="effect1_foregroundBlur_3775_1014"/>
                </filter>
                <radialGradient id="paint0_radial_3775_1014" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(580.5 580.5) rotate(90) scale(580.5)">
                  <stop stopColor="#F9FC93"/>
                </radialGradient>
                <radialGradient id="paint1_radial_3775_1014" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(580.5 580.5) rotate(90) scale(580.5)">
                  <stop stopColor="#BA86FC"/>
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>
        
        {isFormSubmitted ? (
          <div className="absolute inset-0 flex items-center justify-center z-50">
            <div className="flex flex-col items-center text-center">
              <p className="font-manrope font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] mb-0 bg-clip-text text-transparent bg-gradient-to-r from-[#0099fa] via-[#8436ff] to-[#8436ff]">
                Thank you for being with us!
              </p>
              <p className="font-manrope font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] mt-0">
                We will reply shortly.
              </p>
            </div>
          </div>
        ) : (
          <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32 flex flex-col lg:flex-row items-center lg:items-center justify-between gap-8 lg:gap-0 relative z-10">
            <div className="w-full lg:w-[513px] text-center lg:text-left">
              <h2 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] mb-0">
                Let&apos;s Talk<br className="hidden sm:block" />
                About Your Goals & <span className="text-[#8436ff]">Build A Profitable Campaign</span>
              </h2>
            </div>

            <div className="bg-white rounded-[16px] sm:rounded-[24px] shadow-[0px_6px_20px_0px_rgba(0,0,0,0.1)] px-4 sm:px-8 lg:px-[30px] py-8 sm:py-8 lg:py-[30px] w-full max-w-[320px] sm:max-w-full lg:w-[500px] relative z-10">
              <h3 className="font-manrope font-extrabold text-[28px] sm:text-3xl md:text-4xl lg:text-[40px] leading-[1.4] text-[#111827] mb-8 sm:mb-8 lg:mb-[30px]">
                Contact Us
              </h3>
              
              <ContactForm onFormSubmit={() => setIsFormSubmitted(true)} />
            </div>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-white min-h-[200px] lg:h-[250px] w-full border-t border-[#eee] py-8 lg:py-0">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 md:px-8 lg:px-16 xl:px-20 2xl:px-32 h-full flex flex-col lg:flex-row items-center lg:items-center justify-between gap-8 lg:gap-0">
          <div className="flex flex-col gap-4 sm:gap-5 lg:gap-[20px] items-center lg:items-center">
            <div className="h-[50px] w-[90px] sm:h-[60px] sm:w-[110px] lg:h-[76px] lg:w-[135px] relative">
              <Image src="/logo.png" alt="ADV Online" width={135} height={76} className="object-contain" />
            </div>
            <p className="font-manrope font-normal text-xs sm:text-sm lg:text-[13px] text-black">
              © 2025 ADV Online
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-[20px] items-center">
            <Link href="/privacy" className="font-manrope font-semibold text-xs sm:text-sm lg:text-[13px] text-black underline uppercase">
              privacy policy
            </Link>
            <Link href="/terms" className="font-manrope font-semibold text-xs sm:text-sm lg:text-[13px] text-black underline uppercase">
              Terms & Conditions
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
